import { useMemo } from 'react';

import { useGetPubsubEventsQuery } from '../../projects/services/projects.api';
import { useGetBusinessRulesQuery, useUpdateBusinessRuleMutation } from '../services/businessRules.api';
import type { BusinessRuleRow } from '../types/businessRule';
import {
  DEFAULT_NOTIFICATION_CONFIG,
  NOTIFICATION_EVENT_TYPES,
  type DefaultConfig,
  type NotificationConfigValue,
  type NotificationEventType,
} from '../types/notificationConfig';

export const NOTIFICATION_RULE_NAME = 'NOTIFICATION_CONFIG_PROJECT';

interface UseNotificationConfigResult {
  config: NotificationConfigValue | null;
  businessRule: BusinessRuleRow | null;
  businessRuleId: number | null;
  isBusinessRuleEnabled: boolean;
  hasPubsubEvent: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  isSaving: boolean;
  isActive: boolean;
  updateConfig: (nextConfig: NotificationConfigValue) => Promise<void>;
  refetch: () => Promise<unknown>;
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object';
};

const normalizeDefaultConfig = (value: unknown): DefaultConfig => {
  if (!isRecord(value)) {
    return { ...DEFAULT_NOTIFICATION_CONFIG.configs.default };
  }

  const minScoreRaw = value.minScore;
  const considerAssignmentRaw = value.considerAssignment;

  const minScore = typeof minScoreRaw === 'number' && Number.isFinite(minScoreRaw) ? minScoreRaw : 0;
  const considerAssignment = typeof considerAssignmentRaw === 'boolean' ? considerAssignmentRaw : false;

  return {
    minScore: Math.max(0, Math.min(100, minScore)),
    considerAssignment,
  };
};

const normalizeNotificationConfig = (value: unknown): NotificationConfigValue => {
  if (!isRecord(value)) {
    return globalThis.structuredClone(DEFAULT_NOTIFICATION_CONFIG);
  }

  const domains = isRecord(value.domains) ? value.domains : {};
  const configs = isRecord(value.configs) ? value.configs : {};
  const includeUsers = isRecord(value.includeUsers) ? value.includeUsers : {};

  const byProjects: Record<string, DefaultConfig> = {};
  const rawByProjects = isRecord(configs.byProjects) ? configs.byProjects : {};
  Object.entries(rawByProjects).forEach(([projectIdKey, projectConfig]) => {
    byProjects[projectIdKey] = normalizeDefaultConfig(projectConfig);
  });

  const normalizeRoles = (domainValue: unknown): { roles: string[] } | null => {
    if (domainValue === null || domainValue === undefined) {
      return null;
    }

    if (!isRecord(domainValue) || !Array.isArray(domainValue.roles)) {
      return null;
    }

    const roleSet = new Set<string>();
    domainValue.roles.forEach((role) => {
      if (typeof role === 'string' && role.trim()) {
        roleSet.add(role);
      }
    });

    return { roles: [...roleSet] };
  };

  const allUsers =
    Array.isArray(includeUsers.all) && includeUsers.all.every((id) => typeof id === 'number')
      ? includeUsers.all
      : DEFAULT_NOTIFICATION_CONFIG.includeUsers.all;

  const byProjectUser: Record<string, number[]> = {};
  if (isRecord(includeUsers.byProjectUser)) {
    Object.entries(includeUsers.byProjectUser).forEach(([subProjectIdKey, userIds]) => {
      if (!Array.isArray(userIds)) {
        return;
      }

      const uniqueIds = [...new Set(userIds.filter((id) => typeof id === 'number'))];
      byProjectUser[subProjectIdKey] = uniqueIds;
    });
  }

  const byEventType: Partial<Record<NotificationEventType, number[]>> = {};
  if (isRecord(includeUsers.byEventType)) {
    NOTIFICATION_EVENT_TYPES.forEach((eventType) => {
      const userIds = (includeUsers.byEventType as Record<string, unknown>)[eventType];
      if (!Array.isArray(userIds)) {
        return;
      }

      byEventType[eventType] = [...new Set(userIds.filter((id) => typeof id === 'number'))];
    });
  }

  // Event is a pure on/off switch mirroring the notification engine default: absent key = ENABLED, explicit null (UI toggle off) = DISABLED. { roles: [] }
  // is the only meaningful "enabled" value — roles never affect event delivery, the shape just matches the other domains.
  const normalizeEventDomain = (): { roles: string[] } | null => {
    return domains.event === null ? null : { roles: [] };
  };

  return {
    domains: {
      alert: normalizeRoles(domains.alert),
      question: normalizeRoles(domains.question),
      task: normalizeRoles(domains.task),
      event: normalizeEventDomain(),
    },
    configs: {
      default: normalizeDefaultConfig(configs.default),
      byProjects,
    },
    includeUsers: {
      all: [...new Set(allUsers)],
      byProjectUser,
      byEventType,
    },
  };
};

const useNotificationConfig = (projectId: number, enabled = true): UseNotificationConfigResult => {
  const { currentData, isLoading, isFetching, refetch } = useGetBusinessRulesQuery(
    {
      name: NOTIFICATION_RULE_NAME,
      entityType: 'Project',
      entityId: projectId,
      limit: 1,
      page: 1,
      exact: true,
    },
    { skip: !enabled || projectId <= 0 }
  );

  const { currentData: pubsubData, refetch: refetchPubsub } = useGetPubsubEventsQuery(projectId, {
    skip: !enabled || projectId <= 0,
  });

  const [updateBusinessRule, { isLoading: isSaving }] = useUpdateBusinessRuleMutation();

  const businessRule = useMemo((): BusinessRuleRow | null => {
    if (!currentData?.data?.length) {
      return null;
    }

    return currentData.data[0];
  }, [currentData]);

  const normalizedConfig = useMemo(() => {
    if (!businessRule) {
      return null;
    }

    return normalizeNotificationConfig(businessRule.value);
  }, [businessRule]);

  const updateConfig = async (nextConfig: NotificationConfigValue): Promise<void> => {
    if (!businessRule) {
      return;
    }

    await updateBusinessRule({
      id: businessRule.id,
      businessRule: {
        value: nextConfig,
      },
    }).unwrap();
  };

  const hasPubsubEvent = pubsubData?.pubsubEvents?.includes('notifications') ?? false;
  const isBusinessRuleEnabled = businessRule?.enabled ?? false;

  return {
    config: normalizedConfig,
    businessRule,
    businessRuleId: businessRule?.id ?? null,
    isBusinessRuleEnabled,
    hasPubsubEvent,
    isLoading,
    isRefreshing: isFetching && !isLoading,
    isSaving,
    isActive: isBusinessRuleEnabled && hasPubsubEvent,
    updateConfig,
    refetch: async () => Promise.all([refetch(), refetchPubsub()]),
  };
};

export default useNotificationConfig;
